import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import FroalaEditor from 'react-froala-wysiwyg';
import FroalaEditorView from 'react-froala-wysiwyg/FroalaEditorView';
import Froalaeditor from 'froala-editor';
import Tribute from 'tributejs';
import _ from 'lodash';

// Froala Editor JS files.
import 'froala-editor/js/froala_editor.pkgd.min';
import 'froala-editor/js/plugins.pkgd.min';
import 'froala-editor/js/third_party/image_tui.min';
import 'froala-editor/js/third_party/font_awesome.min';

// Froala Editor CSS files.
import 'froala-editor/css/froala_editor.pkgd.min.css';
import 'froala-editor/css/froala_style.min.css';
import 'froala-editor/css/plugins.pkgd.min.css';
import 'froala-editor/css/plugins/video.min.css';
import 'tributejs/dist/tribute.css';
import 'froala-editor/css/third_party/image_tui.min.css';
import 'froala-editor/css/third_party/font_awesome.min.css';

import { captureException } from '@playbookux/playbook-web-core';
import ReportResearchModal from './report-research-modal';
import './styles.css';
import { config } from '../../../config';
import { addResearchButton, froalaConfiguration } from './froala';

Froalaeditor.DefineIconTemplate('text', addResearchButton);
Froalaeditor.DefineIcon('addResearch', { NAME: 'addResearch', template: 'text' });

const CODOX_API_KEY = config.REACT_APP_FROALA_CODOX_API_KEY;

const Froala = (props) => {
  const {
    onReportDataChange,
    reportData,
    onClickAddResearch,
    readOnly,
    modalOpen,
    handleModalOpen,
    companyId,
    docId,
    codoxUser,
    fetchMentions,
    project,
  } = props;

  const [model, setModel] = useState();
  const [initialized, setInitialized] = useState(false);
  const [froalaConfig, setFroalaConfig] = useState(null);

  const [codox, setCodox] = useState(null);
  const [tribute, setTribute] = useState(null);
  const editorRef = useRef(null);

  const handleModelChange = (newModel) => {
    if (/wave.client/ig.test(Error().stack) === false) {
      onReportDataChange(newModel);
    }
  };

  const createVideoTag = useCallback((entityMap, entitySuffix) => {
    if (entityMap && editorRef && editorRef.current.editor.video) {
      // eslint-disable-next-line array-callback-return
      Object.keys(entityMap).map((key) => {
        if (entityMap[key]) {
          const { videoUrl } = entityMap[key];

          try {
            editorRef.current.editor.video.insert(`
              <video controls class="videoContainer" style="width:500px; height: 280px;">
                <source data-id=${key}${entitySuffix} src="${videoUrl}">
              </video>
            `);
          } catch (ex) {
            console.log('Exception going to pocket: ', ex);
          }
        }
      });
      editorRef.current.editor.events.trigger('contentChanged', [], true);
    }
  }, [editorRef]);

  const initializeCodox = useCallback((editor) => {
    if (!codoxUser) return;

    setTimeout(() => {
      if (typeof window.Codox === 'function') {
        try {
          const codoxInstance = new window.Codox();
          codoxInstance.init({
            app: 'froala',
            docId,
            user: codoxUser,
            editor,
            apiKey: CODOX_API_KEY,
            domain: 'playbookux.com',
          });
          setCodox(codoxInstance);
        } catch (error) {
          captureException('CODOX_INIT_FAILED', error);
        }
      }
      // before was 100, increased to 500 because of mount and dismount error
      // that crash the app while clicking the logo icon or the other buttons
    }, 500);
  }, [docId, codoxUser]);

  const initializeTribute = useCallback((editor) => {
    const tributeInstance = new Tribute({
      values: (txt, cb) => fetchMentions(cb),
    });

    tributeInstance.attach(editor.el);
    setTribute(tributeInstance);
  }, [fetchMentions]);

  // Leave codox session upon unmount
  useEffect(() => (
    () => {
      if (codox) {
        codox.stop();
      }
      // FIXME: somehow this line breaks the app, need the real fix here
      if (tribute && editorRef?.current?.el) {
        tribute.detach(editorRef.current.el);
      }
    }
  ), [codox, tribute, editorRef]);

  const handleSetTestSessionNotes = (urlMapTsn) => {
    createVideoTag(urlMapTsn, '_ts');
    handleModalOpen(modalOpen);
  };

  const handleSetHighlightReels = (urlMapHr) => {
    createVideoTag(urlMapHr, '_hr');
    handleModalOpen(modalOpen);
  };

  const insertTextInFroala = useCallback((notesTexts) => {
    if (notesTexts && editorRef && editorRef.current.editor.html) {
      let newText = '';

      _.forEach(notesTexts, (noteText) => {
        if (noteText.content && !_.isEmpty(noteText.tagName)) {
          newText += `<p>${noteText.content} #${_.capitalize(noteText.tagName)}</p>`;
        } else {
          newText += `<p>${noteText.content}</p>`;
        }
      });

      // Must call 'selection.restore()' to know where the value must be inserted.
      editorRef.current.editor.selection.restore();

      // Will insert the value where the current location of the current cursor is.
      editorRef.current.editor.html.insert(newText);

      // After inserting must call 'selection.clear()' to the current cursor do not have weird behavior.
      editorRef.current.editor.selection.clear();

      editorRef.current.editor.events.trigger('contentChanged', [], true);
    }
  }, [editorRef]);

  const handleSetNotes = (notesTexts) => {
    insertTextInFroala(notesTexts);

    handleModalOpen(modalOpen);
  };

  const handleSetTags = (tags) => {
    insertTextInFroala(tags);

    handleModalOpen(modalOpen);
  };

  useEffect(() => {
    // We are manually setting Froala content when we have Codox active
    if (model === undefined && reportData !== undefined && initialized) {
      setModel(reportData);
      editorRef.current.editor.html.set(reportData);
    }

    // When we are in read-only mode, we can set content in Froala normally
    if (readOnly) {
      setModel(reportData);
    }
  }, [model, reportData, initialized, readOnly]);

  useEffect(() => {
    Froalaeditor.RegisterCommand('addResearch', {
      title: 'Add Research',
      focus: false,
      undo: false,
      refreshAfterCallback: false,
      callback: onClickAddResearch,
    });
    setFroalaConfig({
      ...froalaConfiguration,
      events: {
        initialized: function froalaInitialized() {
          initializeCodox(this);
          initializeTribute(this);
          setInitialized(true);
        },
      },
    });
  }, [onClickAddResearch, initializeCodox, initializeTribute, setInitialized]);

  useEffect(() => {
    if (editorRef.current && editorRef.current.editor.selection) {
      editorRef.current.editor.selection.save();
    }
  }, [editorRef]);

  if (readOnly) {
    return (
      <FroalaEditorView
        model={model}
      />
    );
  }

  return (
    <>
      <ReportResearchModal
        companyId={companyId}
        open={modalOpen}
        onClose={handleModalOpen}
        onSetTestSessionNotes={handleSetTestSessionNotes}
        onSetHighlightReels={handleSetHighlightReels}
        onSetNotes={handleSetNotes}
        onSetTags={handleSetTags}
        project={project}
      />
      {froalaConfig && (
        <FroalaEditor
          ref={editorRef}
          tag="textarea"
          config={froalaConfig}
          onModelChange={handleModelChange}
        />
      )}
    </>
  );
};

Froala.propTypes = {
  reportData: PropTypes.string,
  onReportDataChange: PropTypes.func,
  onClickAddResearch: PropTypes.func,
  companyId: PropTypes.string,
  readOnly: PropTypes.bool,
  modalOpen: PropTypes.bool,
  handleModalOpen: PropTypes.func,
  docId: PropTypes.string,
  codoxUser: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  project: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  fetchMentions: PropTypes.func,
};

Froala.defaultProps = {
  readOnly: false,
  reportData: undefined,
  onReportDataChange: undefined,
  onClickAddResearch: undefined,
  companyId: '',
  modalOpen: false,
  handleModalOpen: undefined,
  docId: '',
  codoxUser: {
    id: '',
    name: '',
  },
  fetchMentions: undefined,
};

export default Froala;
